import { CloudUpload, HighlightOff } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Button, ButtonBase, Card, CardContent, Divider, Grid, IconButton, Typography } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { OcrUploadDto } from 'dtos'
import { Formik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import Dropzone, { DropEvent, FileRejection } from 'react-dropzone'

interface OCRDocumentUploadProps {
  refresh: () => void
}

export default function OCRDocumentUploadCard({ refresh }: OCRDocumentUploadProps) {
  return (
    <Formik
      enableReinitialize
      initialValues={new OcrUploadDto()}
      onSubmit={(values, formikHelpers) => {
        controllers.ocr
          .uploadFiles(values)
          .then(response => {
            enqueueSnackbar('Files Uploaded Successfully!', { variant: 'success' })
            formikHelpers.resetForm()
            refresh()
          })
          .catch(error => {})
          .finally(() => {
            formikHelpers.setSubmitting(false)
          })
      }}
      validateOnBlur
      validateOnChange
      // validationSchema={documentValidationSchema}
    >
      {({ errors, handleSubmit, isSubmitting, setFieldValue, values }) => (
        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center'>
                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='h5'>
                    OCR
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant='h5'>Upload Files</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Dropzone
                    onDrop={(acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
                      setFieldValue('files', [...(values.files ?? []), ...acceptedFiles])
                    }}
                    accept={{
                      'application/pdf': ['.pdf']
                    }}
                    multiple
                  >
                    {({ getRootProps, getInputProps }) => (
                      <ButtonBase
                        {...getRootProps()}
                        disabled={isSubmitting}
                        sx={{
                          border: '2px dashed #cecece',
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '16px',
                          width: '100%',
                          textAlign: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <input {...getInputProps()} />
                        <CloudUpload color='disabled' fontSize='large' />
                        <Typography variant='body1'>
                          Drag and drop or
                          <span style={{ color: '#1976d2', textDecoration: 'underline' }}> browse</span>
                          <br />
                          <small>Accepted File Types: .pdf</small>
                        </Typography>
                      </ButtonBase>
                    )}
                  </Dropzone>
                </Grid>

                <Grid item xs={12} container spacing={1} alignItems='center'>
                  <Grid item xs={12}>
                    <Typography variant='body1'>Files To Be Uploaded:</Typography>
                  </Grid>

                  {values.files?.map((file, index) => (
                    <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-start' key={index}>
                      <Grid item>
                        <Typography variant='body2'>{file.name}</Typography>
                      </Grid>

                      <Grid item>
                        <IconButton
                          color='error'
                          disabled={isSubmitting}
                          size='small'
                          onClick={() => {
                            setFieldValue(
                              'files',
                              values.files?.filter((_, i) => i !== index)
                            )
                          }}
                        >
                          <HighlightOff fontSize='small' />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>

                <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-end'>
                  <Grid item alignItems='flex-end'>
                    <Button color='inherit' disabled={isSubmitting} variant='text' onClick={() => {}}>
                      CANCEL
                    </Button>
                  </Grid>

                  <Grid item>
                    <LoadingButton loading={isSubmitting} type='submit' variant='contained'>
                      SUBMIT
                    </LoadingButton>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <ExplanationAccordion variant='outlined'>
                    || The OCR feature allows users to upload PDF Files up to 100 MB in size into VTS2. After documents have been uploaded, user may press
                    Submit to upload the selected files into VTS2 which will also run the OCR process as part of the initial file upload. ||
                  </ExplanationAccordion>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  )
}
